import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Search,
  MapPin,
  Briefcase,
  DollarSign,
  Clock,
  Building2,
  Globe,
  Sparkles,
  Send,
  Heart,
  ExternalLink,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Filter,
  Zap,
  Target,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Loader2,
  BookOpen,
  FileText,
  Users,
  Rocket,
  Brain,
  Lightbulb,
  Award,
  Star,
  Wifi,
  Leaf,
  RefreshCw,
  Radio,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CareerApi, AIApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import QoreConnectPageHero from './QoreConnectPageHero';
import { PAGE_META } from './qoreConnectConfig';
import './JobBoard.css';
import './QoreConnectPages.css';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const company = typeof raw.company === 'string' ? { name: raw.company } : (raw.company || {});
  const skills = Array.isArray(raw.requiredSkills)
    ? raw.requiredSkills.map((s) => (typeof s === 'string' ? { name: s } : s)).filter((s) => s?.name)
    : Array.isArray(raw.tags)
      ? raw.tags.map((name) => ({ name }))
      : [];
  const loc = raw.location;
  return {
    ...raw,
    id: raw.id || raw._id || raw.source?.externalId,
    title: raw.title || raw.position || 'Untitled role',
    company: {
      name: company.name || raw.company_name || 'Company',
      logo: company.logo || raw.company_logo,
    },
    location: {
      city: loc?.city || loc?.name || (typeof loc === 'string' ? loc : '') || loc?.country || '',
      country: loc?.country || '',
      remote: !!(loc?.remote || raw.remote || raw.metadata?.isRemoteFriendly),
      remoteType: loc?.remoteType,
    },
    requiredSkills: skills,
    source: raw.source || {
      platform: raw.platform,
      url: raw.url || raw.apply_url,
      postedDate: raw.postedDate || raw.createdAt,
    },
    description: typeof raw.description === 'string' ? raw.description : '',
  };
};

const JobBoard = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const meta = PAGE_META.jobs;
  
  // State management
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [marketStats, setMarketStats] = useState(null);
  
  // Debounced search values (500ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedLocation = useDebounce(locationFilter, 500);
  
  // Real-time job sources
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState(['arbeitnow', 'themuse', 'remoteok', 'remotive']);
  const [isRealTime, setIsRealTime] = useState(true);
  const [sourceStats, setSourceStats] = useState({});
  const [fetchError, setFetchError] = useState('');
  const [showSourcePanel, setShowSourcePanel] = useState(false);
  
  // AI Chat State
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGuidance, setAiGuidance] = useState(null);
  const aiMessagesRef = useRef(null);
  
  // Quick filters
  const [activeFilters, setActiveFilters] = useState([]);
  
  const jobsPerPage = 12;

  // Fetch available job sources
  const fetchSources = async () => {
    try {
      const response = await CareerApi.getAvailableSources();
      setAvailableSources(response.data || []);
    } catch (error) {
      console.error('Error fetching sources:', error);
      setAvailableSources([
        { id: 'remoteok', name: 'RemoteOK', available: true, icon: '🌍' },
        { id: 'arbeitnow', name: 'Arbeitnow', available: true, icon: '🇪🇺' },
        { id: 'themuse', name: 'The Muse', available: true, icon: '🎯' },
      ]);
    }
  };

  // Fetch real-time jobs from external APIs
  const applyClientFilters = (list) => {
    let jobsData = list.map(normalizeJob).filter(Boolean);
    if (experienceFilter) {
      const exp = experienceFilter.toLowerCase();
      jobsData = jobsData.filter((job) => String(job.experienceLevel || '').toLowerCase().includes(exp));
    }
    if (remoteOnly || activeFilters.includes('remote')) {
      jobsData = jobsData.filter((job) => job.location?.remote || job.metadata?.isRemoteFriendly);
    }
    if (activeFilters.includes('green')) {
      const green = jobsData.filter((job) => job.metadata?.isGreenJob);
      if (green.length) jobsData = green;
    }
    if (activeFilters.includes('africa')) {
      const africa = jobsData.filter((job) => job.metadata?.isAfricanOpportunity || /africa|nigeria|kenya|ghana|rwanda|south africa/i.test(`${job.location?.city} ${job.location?.country}`));
      if (africa.length) jobsData = africa;
    }
    if (activeFilters.includes('apply')) {
      jobsData = jobsData.filter((job) => job.source?.url);
    }
    if (sortBy === 'salary') {
      jobsData.sort((a, b) => (b.salary?.max || 0) - (a.salary?.max || 0));
    }
    return jobsData;
  };

  const fetchRealTimeJobs = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const response = await CareerApi.getRealTimeJobs({
        query: (debouncedSearchQuery || '').trim(),
        sources: (selectedSources.length ? selectedSources : ['arbeitnow', 'themuse', 'remoteok', 'remotive']).join(','),
        location: (debouncedLocation || '').trim(),
        limit: 80,
      });

      const jobsData = applyClientFilters(response.data || []);
      setSourceStats(response.sources || {});
      setJobs(jobsData);
      if (!jobsData.length) {
        setFetchError('No roles matched those filters. Try clearing search or switching sources.');
      }
    } catch (error) {
      console.error('Error fetching real-time jobs:', error);
      setFetchError(error.message || 'Could not reach live job sources.');
      try {
        const fallback = await CareerApi.getRecentJobs({ limit: 50 });
        const jobsData = applyClientFilters(fallback.data || []);
        if (jobsData.length) setJobs(jobsData);
      } catch {
        /* keep previous list */
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, debouncedLocation, experienceFilter, remoteOnly, activeFilters, sortBy, selectedSources]);

  // Fetch cached jobs (fallback)
  const fetchCachedJobs = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      
      if (debouncedSearchQuery || debouncedLocation) {
        response = await CareerApi.searchJobs({
          query: debouncedSearchQuery || 'engineer',
          location: debouncedLocation || null,
          remote: remoteOnly,
          limit: 50
        });
      } else {
        response = await CareerApi.getRecentJobs({
          limit: 50,
          region: activeFilters.includes('africa') ? 'Africa' : null
        });
      }
      
      const jobsData = applyClientFilters(response.data || []);
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching cached jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, debouncedLocation, experienceFilter, remoteOnly, activeFilters, sortBy, user]);

  // Fetch market statistics
  const fetchMarketStats = async () => {
    try {
      const response = await CareerApi.getMarketStats();
      setMarketStats(response.data);
    } catch (error) {
      console.error('Error fetching market stats:', error);
      setMarketStats({
        totalJobs: 500,
        remoteJobs: 180,
        africanJobs: 120,
        greenJobs: 75
      });
    }
  };

  // Initial load
  useEffect(() => {
    fetchSources();
    fetchMarketStats();
  }, []);

  // Fetch jobs when dependencies change
  useEffect(() => {
    if (isRealTime) {
      fetchRealTimeJobs();
    } else {
      fetchCachedJobs();
    }
  }, [isRealTime, fetchRealTimeJobs, fetchCachedJobs]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    if (isRealTime) {
      fetchRealTimeJobs();
    } else {
      fetchCachedJobs();
    }
  };

  // Toggle source selection
  const toggleSource = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        // Don't allow deselecting all sources
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== sourceId);
      }
      return [...prev, sourceId];
    });
  };

  // Toggle quick filter
  const toggleFilter = (filter) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  // Toggle save job
  const toggleSaveJob = (jobId) => {
    setSavedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  // AI Chat functionality
  const sendAiMessage = async (message) => {
    if (!message.trim()) return;
    
    const userMessage = { role: 'user', content: message };
    setAiMessages(prev => [...prev, userMessage]);
    setAiInput('');
    setAiLoading(true);
    
    try {
      const response = await AIApi.chat(token, {
        messages: [...aiMessages, userMessage],
        context: 'job_search',
        systemPrompt: `You are QoreNova's AI Career Assistant. Help users with job searching, application tips, resume advice, and career guidance. Be concise, helpful, and encouraging. Current job search: "${searchQuery || 'all jobs'}". If asked about specific jobs, provide general industry advice.`
      });
      
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data?.response || response.message || "I'm here to help with your job search! Ask me about resume tips, interview prep, or career advice."
      }]);
    } catch (error) {
      console.error('AI Chat error:', error);
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I can help you with job searching strategies, resume optimization, interview preparation, and career development tips. What would you like to know?"
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Get AI guidance for selected job
  const getJobGuidance = async (job) => {
    if (!job) return;
    
    setAiGuidance({ loading: true });
    
    try {
      const response = await AIApi.chat(token, {
        messages: [{
          role: 'user',
          content: `Provide concise application tips for this job: ${job.title} at ${job.company?.name}. Required skills: ${job.requiredSkills?.map(s => s.name).join(', ') || 'Not specified'}. Experience level: ${job.experienceLevel}. Give 3 specific tips.`
        }],
        context: 'job_application'
      });
      
      setAiGuidance({
        loading: false,
        tips: response.data?.response || response.message || generateDefaultTips(job)
      });
    } catch (error) {
      console.error('AI guidance error:', error);
      setAiGuidance({
        loading: false,
        tips: generateDefaultTips(job)
      });
    }
  };

  const generateDefaultTips = (job) => {
    return `1. Highlight your experience with ${job.requiredSkills?.[0]?.name || 'relevant technologies'} in your resume header.
    
2. Research ${job.company?.name || 'the company'} and mention specific projects or values that resonate with you in your cover letter.

3. Prepare examples demonstrating your ${job.experienceLevel || 'professional'}-level problem-solving abilities for the interview.`;
  };

  // Quick AI suggestions
  const aiSuggestions = [
    "How do I optimize my resume?",
    "Interview tips for engineers",
    "Salary negotiation advice",
    "Best skills to learn in 2026"
  ];

  // Format salary display
  const formatSalary = (salary) => {
    if (!salary || !salary.disclosed) return 'Salary not disclosed';
    
    const formatNum = (num) => {
      if (num >= 1000) return `${(num / 1000).toFixed(0)}k`;
      return num;
    };
    
    const currency = salary.currency === 'NGN' ? '₦' : salary.currency === 'EUR' ? '€' : salary.currency === 'GBP' ? '£' : '$';
    const period = salary.period === 'monthly' ? '/mo' : '/yr';
    
    if (salary.min && salary.max && salary.min !== salary.max) {
      return `${currency}${formatNum(salary.min)} - ${currency}${formatNum(salary.max)}${period}`;
    }
    return `${currency}${formatNum(salary.max || salary.min)}${period}`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Recently';
    const d = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
    return d.toLocaleDateString();
  };

  // Get company initials for logo fallback
  const getCompanyInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  };

  // Pagination
  const totalPages = Math.ceil(jobs.length / jobsPerPage);
  const paginatedJobs = jobs.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);

  // Scroll AI messages
  useEffect(() => {
    if (aiMessagesRef.current) {
      aiMessagesRef.current.scrollTop = aiMessagesRef.current.scrollHeight;
    }
  }, [aiMessages]);

  return (
    <div className="job-board-container qc-page qc-page--jobs">
      <QoreConnectPageHero accent={meta.accent} {...meta} />

      <section className="qc-jobs-controls">
        <div className="qc-jobs-controls__inner">
          <div className="qc-jobs-controls__modes">
            <button
              type="button"
              className={`qc-jobs-mode-btn ${isRealTime ? 'qc-jobs-mode-btn--live' : ''}`}
              onClick={() => setIsRealTime(!isRealTime)}
            >
              <Radio size={16} style={{ animation: isRealTime ? 'pulse 2s infinite' : 'none' }} />
              {isRealTime ? t('qoreConnect.pages.jobs.liveMode') : t('qoreConnect.pages.jobs.cachedMode')}
            </button>

            <button
              type="button"
              className="qc-jobs-mode-btn qc-jobs-mode-btn--sources"
              onClick={() => setShowSourcePanel(!showSourcePanel)}
            >
              <Globe size={16} />
              {t('qoreConnect.pages.jobs.sources', { count: selectedSources.length })}
              <ChevronDown size={16} style={{ transform: showSourcePanel ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
            </button>

            {isRealTime && (
              <button
                type="button"
                className="qc-jobs-mode-btn qc-jobs-mode-btn--refresh"
                onClick={fetchRealTimeJobs}
                disabled={loading}
              >
                <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                {t('qoreConnect.pages.jobs.refresh')}
              </button>
            )}
          </div>

          {/* Source Panel */}
          {showSourcePanel && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.95)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              maxWidth: '700px',
              margin: '0 auto 24px'
            }}>
              <h4 style={{ color: 'white', marginBottom: '16px', fontSize: '14px', fontWeight: '600' }}>
                🌐 Select Job Sources (Real-time data from these platforms):
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {availableSources.map(source => (
                  <button
                    key={source.id}
                    onClick={() => toggleSource(source.id)}
                    disabled={!source.available}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      background: selectedSources.includes(source.id) 
                        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
                        : source.available 
                          ? 'rgba(51, 65, 85, 0.8)' 
                          : 'rgba(51, 65, 85, 0.3)',
                      border: selectedSources.includes(source.id) 
                        ? '1px solid #818cf8' 
                        : '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '12px',
                      color: source.available ? 'white' : '#64748b',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: source.available ? 'pointer' : 'not-allowed',
                      opacity: source.available ? 1 : 0.5,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{source.icon}</span>
                    {source.name}
                    {!source.available && source.requiresKey && (
                      <span style={{ fontSize: '10px', color: '#f59e0b' }}>API Key Required</span>
                    )}
                    {selectedSources.includes(source.id) && <CheckCircle size={14} />}
                  </button>
                ))}
              </div>
              {Object.keys(sourceStats).length > 0 && (
                <div style={{ marginTop: '16px', fontSize: '12px', color: '#94a3b8' }}>
                  <strong>Last fetch:</strong> {Object.entries(sourceStats).map(([src, stat]) => (
                    <span key={src} style={{ marginLeft: '10px' }}>
                      {src}: {typeof stat === 'object' && stat !== null 
                        ? `${stat.count || 0} jobs` 
                        : typeof stat === 'number' 
                          ? `${stat} jobs` 
                          : stat}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Search Bar */}
          <div className="job-search-container">
            <form onSubmit={handleSearch} className="job-search-box">
              <div className="job-search-input-wrapper">
                <Search size={20} />
                <input
                  type="text"
                  className="job-search-input"
                  placeholder={t('qoreConnect.pages.jobs.searchTitle')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="job-search-input-wrapper">
                <MapPin size={20} />
                <input
                  type="text"
                  className="job-search-input"
                  placeholder={t('qoreConnect.pages.jobs.searchLocation')}
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                />
              </div>
              
              <select 
                className="job-search-select"
                value={experienceFilter}
                onChange={(e) => setExperienceFilter(e.target.value)}
              >
                <option value="">All Levels</option>
                <option value="entry">Entry Level</option>
                <option value="junior">Junior</option>
                <option value="mid">Mid-Level</option>
                <option value="senior">Senior</option>
                <option value="lead">Lead / Principal</option>
                <option value="executive">Executive</option>
              </select>
              
              <button type="submit" className="job-search-btn" disabled={loading}>
                {loading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                {isRealTime ? t('qoreConnect.pages.jobs.fetchLive') : t('qoreConnect.pages.jobs.searchBtn')}
              </button>
            </form>
          </div>
          
          {/* Quick Filters */}
          <div className="job-quick-filters">
            <button 
              className={`job-filter-chip ${activeFilters.includes('remote') ? 'active' : ''}`}
              onClick={() => toggleFilter('remote')}
            >
              <Wifi size={16} />
              {t('qoreConnect.pages.jobs.remoteOnly')}
            </button>
            <button 
              className={`job-filter-chip ${activeFilters.includes('africa') ? 'active' : ''}`}
              onClick={() => toggleFilter('africa')}
            >
              <Globe size={16} />
              {t('qoreConnect.pages.jobs.africaJobs')}
            </button>
            <button 
              className={`job-filter-chip ${activeFilters.includes('green') ? 'active' : ''}`}
              onClick={() => toggleFilter('green')}
            >
              <Leaf size={16} />
              {t('qoreConnect.pages.jobs.greenEnergy')}
            </button>
            <button 
              className={`job-filter-chip ${activeFilters.includes('apply') ? 'active' : ''}`}
              onClick={() => toggleFilter('apply')}
            >
              <Zap size={16} />
              Easy apply
            </button>
          </div>
          
          {/* Stats Bar */}
          <div className="job-stats-bar">
            <div className="job-stat-item">
              <div className="job-stat-value">{jobs.length || marketStats?.totalJobs?.toLocaleString() || '0'}</div>
              <div className="job-stat-label">{isRealTime ? 'Live Jobs Found' : 'Active Jobs'}</div>
            </div>
            <div className="job-stat-item">
              <div className="job-stat-value">{jobs.filter(j => j.location?.remote).length || marketStats?.remoteJobs?.toLocaleString() || '0'}</div>
              <div className="job-stat-label">Remote Positions</div>
            </div>
            <div className="job-stat-item">
              <div className="job-stat-value">{selectedSources.length}</div>
              <div className="job-stat-label">Active Sources</div>
            </div>
            <div className="job-stat-item">
              <div className="job-stat-value">{isRealTime ? '⚡ Live' : '📁 Cached'}</div>
              <div className="job-stat-label">Data Mode</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <div className="job-main-content">
        {/* Job Listings */}
        <div className="job-list-section">
          <div className="job-list-header">
            <div>
              <h2 className="job-list-title">{t('qoreConnect.pages.jobs.listTitle')}</h2>
              <span className="job-list-count">{t('qoreConnect.pages.jobs.jobsFound', { count: jobs.length })}</span>
            </div>
            <select 
              className="job-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">{t('qoreConnect.pages.jobs.sortRecent')}</option>
              <option value="salary">{t('qoreConnect.pages.jobs.sortSalary')}</option>
              <option value="match">{t('qoreConnect.pages.jobs.sortMatch')}</option>
            </select>
          </div>
          
          {/* Loading State */}
          {loading && (
            <div className="job-loading">
              <div className="job-loading-spinner"></div>
              <p>{t('qoreConnect.pages.jobs.loading')}</p>
            </div>
          )}
          
          {/* Empty State */}
          {!loading && jobs.length === 0 && (
            <div className="job-empty-state">
              <div className="job-empty-icon">
                <Briefcase size={40} />
              </div>
              <h3 className="job-empty-title">{t('qoreConnect.pages.jobs.emptyTitle')}</h3>
              <p className="job-empty-text">{fetchError || t('qoreConnect.pages.jobs.emptyDesc')}</p>
              <button type="button" className="job-search-btn" onClick={fetchRealTimeJobs} style={{ marginTop: 12 }}>
                <RefreshCw size={16} /> Try again
              </button>
            </div>
          )}
          {fetchError && jobs.length > 0 && (
            <p className="job-empty-text" style={{ marginBottom: 12 }}>{fetchError}</p>
          )}
          
          {/* Job Cards */}
          {!loading && paginatedJobs.map((job, index) => (
            <div 
              key={job.id || job._id || `job-${index}`} 
              className="job-card"
              onClick={() => {
                setSelectedJob(job);
                getJobGuidance(job);
              }}
            >
              <div className="job-card-header">
                <div className="job-company-logo">
                  {job.company?.logo ? (
                    <img src={job.company.logo} alt={job.company.name} />
                  ) : (
                    getCompanyInitials(job.company?.name)
                  )}
                </div>
                <div className="job-card-info">
                  <h3 className="job-title">{job.title}</h3>
                  <p className="job-company-name">{job.company?.name || 'Company'}</p>
                </div>
                <div className="job-card-badges">
                  {job.location?.remote && (
                    <span className="job-badge job-badge-remote">Remote</span>
                  )}
                  {job.source?.postedDate && new Date(job.source.postedDate) > new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) && (
                    <span className="job-badge job-badge-new">New</span>
                  )}
                  {job.metadata?.isGreenJob && (
                    <span className="job-badge job-badge-featured">🌱 Green</span>
                  )}
                </div>
              </div>
              
              <div className="job-card-meta">
                <span className="job-meta-item">
                  <MapPin size={16} />
                  {job.location?.city || job.location?.country || 'Location TBD'}
                </span>
                <span className="job-meta-item">
                  <Briefcase size={16} />
                  {job.employmentType?.replace('-', ' ') || 'Full-time'}
                </span>
                <span className="job-meta-item">
                  <Clock size={16} />
                  {formatDate(job.source?.postedDate)}
                </span>
                {job.experienceLevel && (
                  <span className="job-meta-item">
                    <Target size={16} />
                    {job.experienceLevel.charAt(0).toUpperCase() + job.experienceLevel.slice(1)}
                  </span>
                )}
              </div>
              
              {job.requiredSkills?.length > 0 && (
                <div className="job-card-skills">
                  {job.requiredSkills.slice(0, 5).map((skill, i) => (
                    <span 
                      key={i} 
                      className={`job-skill-tag ${user?.skills?.includes(skill.name) ? 'match' : ''}`}
                    >
                      {skill.name}
                    </span>
                  ))}
                  {job.requiredSkills.length > 5 && (
                    <span className="job-skill-tag">+{job.requiredSkills.length - 5} more</span>
                  )}
                </div>
              )}
              
              {job.description && (
                <p className="job-card-description">{job.description}</p>
              )}
              
              <div className="job-card-footer">
                <div className="job-salary">
                  {formatSalary(job.salary)}
                  {job.salary?.disclosed && (
                    <span className="job-salary-period"> {job.salary.period === 'monthly' ? '/month' : '/year'}</span>
                  )}
                </div>
                <div className="job-card-actions">
                  <span className={`job-source-pill job-source-${job.source?.platform || 'internal'}`}>
                    📍 {job.source?.platform === 'arbeitnow' ? 'Arbeitnow' : 
                        job.source?.platform === 'themuse' ? 'The Muse' : 
                        job.source?.platform === 'jsearch' ? job.source?.originalPlatform || 'JSearch' : 
                        job.source?.platform || 'QoreNova'}
                  </span>
                  {job.source?.url && (
                    <a 
                      href={job.source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="job-apply-btn-external"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} /> Apply
                    </a>
                  )}
                  <button 
                    className={`job-save-btn ${savedJobs.has(job.id || job._id) ? 'saved' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSaveJob(job.id || job._id);
                    }}
                  >
                    <Heart size={18} fill={savedJobs.has(job.id || job._id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="job-pagination">
              <button 
                className="job-page-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={20} />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`job-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button 
                className="job-page-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <aside className="job-sidebar">
          {/* AI Career Assistant */}
          <div className="job-sidebar-card job-ai-card">
            <div className="job-ai-icon">
              <Brain size={24} color="white" />
            </div>
            <h3 className="job-sidebar-title">
              AI Career Assistant
            </h3>
            <p style={{ color: 'var(--job-text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
              Get personalized career advice, resume tips, and application strategies.
            </p>
            
            {/* AI Messages */}
            {aiMessages.length > 0 && (
              <div className="job-ai-messages" ref={aiMessagesRef}>
                {aiMessages.map((msg, i) => (
                  <div 
                    key={i} 
                    className={`job-ai-message ${msg.role === 'user' ? 'job-ai-message-user' : 'job-ai-message-ai'}`}
                  >
                    {msg.content}
                  </div>
                ))}
                {aiLoading && (
                  <div className="job-ai-typing">
                    <span className="job-ai-typing-dot"></span>
                    <span className="job-ai-typing-dot"></span>
                    <span className="job-ai-typing-dot"></span>
                  </div>
                )}
              </div>
            )}
            
            {/* AI Input */}
            <div className="job-ai-input-wrapper">
              <input
                type="text"
                className="job-ai-input"
                placeholder="Ask me anything about your career..."
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendAiMessage(aiInput)}
              />
              <button 
                className="job-ai-send-btn"
                onClick={() => sendAiMessage(aiInput)}
                disabled={!aiInput.trim() || aiLoading}
              >
                <Send size={18} />
              </button>
            </div>
            
            {/* Quick Suggestions */}
            {aiMessages.length === 0 && (
              <div className="job-ai-suggestions">
                {aiSuggestions.map((suggestion, i) => (
                  <button 
                    key={i}
                    className="job-ai-suggestion"
                    onClick={() => sendAiMessage(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Trending Skills */}
          <div className="job-sidebar-card">
            <h3 className="job-sidebar-title">
              <TrendingUp size={20} />
              In-Demand Skills
            </h3>
            <div className="job-skills-list">
              {[
                { name: 'AI / Machine Learning', demand: 95, trend: '+15%' },
                { name: 'Cloud (AWS/Azure/GCP)', demand: 88, trend: '+12%' },
                { name: 'React / TypeScript', demand: 82, trend: '+8%' },
                { name: 'DevOps / CI-CD', demand: 78, trend: '+10%' },
                { name: 'Python', demand: 85, trend: '+5%' },
              ].map((skill, i) => (
                <div key={i} className="job-skill-match-item">
                  <div className={`job-skill-match-icon ${i < 3 ? '' : 'missing'}`}>
                    {i < 3 ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  </div>
                  <div className="job-skill-match-info">
                    <div className="job-skill-match-name">{skill.name}</div>
                    <div className="job-skill-match-demand">{skill.trend} this year</div>
                  </div>
                  <div className="job-skill-match-bar">
                    <div 
                      className="job-skill-match-bar-fill" 
                      style={{ width: `${skill.demand}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Your Applications */}
          {user && (
            <div className="job-sidebar-card">
              <h3 className="job-sidebar-title">
                <FileText size={20} />
                Your Applications
              </h3>
              {savedJobs.size > 0 ? (
                <div className="job-app-tracker">
                  {Array.from(savedJobs).slice(0, 3).map((jobId, i) => {
                    const job = jobs.find(j => j._id === jobId);
                    if (!job) return null;
                    return (
                      <div key={i} className="job-app-item">
                        <span className={`job-app-status ${['pending', 'reviewed', 'interview'][i % 3]}`}></span>
                        <div className="job-app-info">
                          <div className="job-app-title">{job.title?.substring(0, 25)}...</div>
                          <div className="job-app-company">{job.company?.name}</div>
                        </div>
                        <span className="job-app-date">Saved</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: 'var(--job-text-muted)', fontSize: '14px' }}>
                  Save jobs to track your applications here.
                </p>
              )}
            </div>
          )}
          
          {/* Career Resources */}
          <div className="job-sidebar-card">
            <h3 className="job-sidebar-title">
              <BookOpen size={20} />
              Career Resources
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a href="/mentorship" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--job-text-secondary)', textDecoration: 'none', padding: '10px', background: 'var(--job-bg-primary)', borderRadius: 'var(--job-radius-md)' }}>
                <Users size={18} color="var(--job-primary)" />
                <span>Find a Mentor</span>
              </a>
              <a href="/learning" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--job-text-secondary)', textDecoration: 'none', padding: '10px', background: 'var(--job-bg-primary)', borderRadius: 'var(--job-radius-md)' }}>
                <Rocket size={18} color="var(--job-primary)" />
                <span>Skill Courses</span>
              </a>
              <a href="/virtual-labs" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--job-text-secondary)', textDecoration: 'none', padding: '10px', background: 'var(--job-bg-primary)', borderRadius: 'var(--job-radius-md)' }}>
                <Lightbulb size={18} color="var(--job-primary)" />
                <span>Virtual Labs</span>
              </a>
            </div>
          </div>
        </aside>
      </div>
      
      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="job-modal-overlay" onClick={() => setSelectedJob(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()}>
            <button className="job-modal-close" onClick={() => setSelectedJob(null)}>
              <X size={20} />
            </button>
            
            <div className="job-modal-header">
              <div className="job-modal-company-row">
                <div className="job-modal-logo">
                  {selectedJob.company?.logo ? (
                    <img src={selectedJob.company.logo} alt={selectedJob.company.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                  ) : (
                    getCompanyInitials(selectedJob.company?.name)
                  )}
                </div>
                <div>
                  <h2 className="job-modal-title">{selectedJob.title}</h2>
                  <p className="job-modal-company">{selectedJob.company?.name}</p>
                </div>
              </div>
              
              <div className="job-modal-meta">
                <span className="job-meta-item">
                  <MapPin size={16} />
                  {selectedJob.location?.city}, {selectedJob.location?.country}
                </span>
                <span className="job-meta-item">
                  <Briefcase size={16} />
                  {selectedJob.employmentType?.replace('-', ' ')}
                </span>
                <span className="job-meta-item">
                  <DollarSign size={16} />
                  {formatSalary(selectedJob.salary)}
                </span>
                {selectedJob.location?.remote && (
                  <span className="job-badge job-badge-remote">Remote</span>
                )}
              </div>
            </div>
            
            <div className="job-modal-body">
              {/* AI Application Guidance */}
              <div className="job-ai-guidance">
                <div className="job-ai-guidance-header">
                  <div className="job-ai-guidance-icon">
                    <Sparkles size={20} color="white" />
                  </div>
                  <div>
                    <h4 className="job-ai-guidance-title">AI Application Guidance</h4>
                    <span style={{ fontSize: '12px', color: 'var(--job-text-muted)' }}>Personalized tips for this role</span>
                  </div>
                </div>
                
                {aiGuidance?.loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--job-text-muted)' }}>
                    <Loader2 size={16} className="animate-spin" />
                    Generating personalized tips...
                  </div>
                ) : (
                  <div className="job-ai-guidance-content">
                    {aiGuidance?.tips?.split('\n').filter(t => t.trim()).map((tip, i) => (
                      <div key={i} className="job-ai-guidance-tip">
                        <CheckCircle size={16} className="job-ai-guidance-tip-icon" />
                        <span>{tip.replace(/^\d+\.\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div className="job-modal-section">
                <h3 className="job-modal-section-title">
                  <FileText size={18} />
                  About the Role
                </h3>
                <p className="job-modal-description">
                  {selectedJob.description || 'No description provided.'}
                </p>
              </div>
              
              {/* Requirements */}
              {selectedJob.requirements?.length > 0 && (
                <div className="job-modal-section">
                  <h3 className="job-modal-section-title">
                    <Target size={18} />
                    Requirements
                  </h3>
                  <ul className="job-modal-list">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Skills */}
              {selectedJob.requiredSkills?.length > 0 && (
                <div className="job-modal-section">
                  <h3 className="job-modal-section-title">
                    <Award size={18} />
                    Required Skills
                  </h3>
                  <div className="job-card-skills">
                    {selectedJob.requiredSkills.map((skill, i) => (
                      <span 
                        key={i} 
                        className={`job-skill-tag ${skill.importance === 'required' ? 'match' : ''}`}
                        title={`${skill.level || 'Any'} level - ${skill.importance || 'Preferred'}`}
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Responsibilities */}
              {selectedJob.responsibilities?.length > 0 && (
                <div className="job-modal-section">
                  <h3 className="job-modal-section-title">
                    <Briefcase size={18} />
                    Responsibilities
                  </h3>
                  <ul className="job-modal-list">
                    {selectedJob.responsibilities.map((resp, i) => (
                      <li key={i}>{resp}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Company Info */}
              <div className="job-modal-section">
                <h3 className="job-modal-section-title">
                  <Building2 size={18} />
                  About {selectedJob.company?.name}
                </h3>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {selectedJob.company?.industry && (
                    <span className="job-meta-item">
                      <Globe size={16} />
                      {selectedJob.company.industry}
                    </span>
                  )}
                  {selectedJob.company?.size && (
                    <span className="job-meta-item">
                      <Users size={16} />
                      {selectedJob.company.size.charAt(0).toUpperCase() + selectedJob.company.size.slice(1)} company
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="job-modal-footer">
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  className={`job-save-btn ${savedJobs.has(selectedJob._id) ? 'saved' : ''}`}
                  onClick={() => toggleSaveJob(selectedJob._id)}
                  style={{ width: 'auto', borderRadius: 'var(--job-radius-md)', padding: '10px 16px' }}
                >
                  <Heart size={18} fill={savedJobs.has(selectedJob._id) ? 'currentColor' : 'none'} />
                  {savedJobs.has(selectedJob._id) ? 'Saved' : 'Save Job'}
                </button>
              </div>
              
              <a 
                href={selectedJob.source?.url || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="job-apply-btn"
              >
                Apply Now
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default JobBoard;
